/**
 * Formats a date as a local `YYYY-MM-DD` string using local date components.
 *
 * Prefer this over `new Date().toISOString().split('T')[0]`, which serializes in UTC and
 * rolls the date back a day for timezones ahead of UTC (e.g. AEST) whenever the local time
 * is earlier than the UTC offset.
 *
 * @param date - The date to format; defaults to now.
 * @returns The local calendar date as `YYYY-MM-DD`.
 */
export function toLocalDateString(date: Date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Converts a picked date to the ISO instant of UTC midnight on the calendar day it names.
 *
 * A date-only value serialized with `toISOString()` lands on the previous UTC day whenever the
 * picker ran in a timezone ahead of UTC, and both the server rule and `nextServiceDateResultFor` compare
 * UTC days -- so the day the technician chose came back as an earlier one. Anchoring the chosen
 * calendar day to UTC midnight keeps it intact on both sides.
 *
 * A string is read literally rather than parsed, because date-only strings parse as UTC while
 * picker strings without an offset parse as local -- reading the digits is the same answer in
 * every timezone.
 *
 * @param date - The picked date, or an ISO string whose leading date is the chosen day.
 * @returns The ISO instant of UTC midnight for that calendar day.
 */
export function toUtcStartOfDayIso(date: Date | string): string {
    if (typeof date === 'string') {
        const named = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]|$)/.exec(date);

        if (named) {
            return `${named[1]}-${named[2]}-${named[3]}T00:00:00.000Z`;
        }
    }

    const picked = date instanceof Date ? date : new Date(date);

    return new Date(Date.UTC(picked.getFullYear(), picked.getMonth(), picked.getDate())).toISOString();
}

export function GetFullDateRange(startDate: Date, endDate: Date | null = null): { startDate: Date; endDate: Date } {
    startDate.setHours(0, 0, 0, 0); // Set to start of day
    endDate = endDate ?? new Date(startDate);

    endDate.setDate(endDate.getDate() + 1); // Move to next day
    endDate.setHours(0, 0, 0, 0); // Set to start of next day
    return { startDate, endDate };
}
